import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useActiveTenant } from './useActiveTenant';

interface ImpersonationData {
  clientId: string;
  clientName: string;
  userEmail: string;
  userId: string;
  startedAt: string;
}

/**
 * Hook to get impersonated client information
 * Returns the impersonated clientId and userId if impersonating,
 * otherwise returns the real clientId and userId
 */
export function useImpersonatedClient() {
  const { user } = useAuth();
  const { activeTenantId, loading: tenantLoading } = useActiveTenant();
  const [impersonation, setImpersonation] = useState<ImpersonationData | null>(null);

  // Check for impersonation data
  useEffect(() => {
    const checkImpersonation = () => {
      const data = localStorage.getItem('admin_impersonation');
      if (data) {
        try {
          const parsed = JSON.parse(data);
          setImpersonation(parsed);
        } catch (e) {
          console.error('Invalid impersonation data:', e);
          localStorage.removeItem('admin_impersonation');
          setImpersonation(null);
        }
      } else {
        setImpersonation(null);
      }
    };

    checkImpersonation();

    // Listen for storage changes (in case another tab modifies it)
    window.addEventListener('storage', checkImpersonation);
    window.addEventListener('impersonation-change', checkImpersonation);
    return () => {
      window.removeEventListener('storage', checkImpersonation);
      window.removeEventListener('impersonation-change', checkImpersonation);
    };
  }, []);

  const isImpersonating = !!impersonation;
  const impersonatedClientId = impersonation?.clientId || null;
  const impersonatedUserId = impersonation?.userId || null;

  // Return impersonated values if impersonating, otherwise real values
  return {
    isImpersonating,
    clientId: impersonatedClientId || activeTenantId,
    userId: impersonatedUserId || user?.id || null,
    impersonatedClientId,
    impersonatedUserId,
    impersonationData: impersonation,
    loading: !isImpersonating && tenantLoading,
  };
}
